import { capitalize } from "@/api/helpers";
import { notEmptyString, validDateString } from "@/api/validators";
import { KeyAnyValue, SimpleProject, defaultSimpleProject } from "./types";

export class UserSchema {
  _id: "";
  identifier = "";
  fullName = "";
  role = "artist";
  mode = "local";
  active = false;
  lastLogin = new Date();
  created = new Date();
  modified = new Date();
  [key: string]: any;

  constructor(data = null) {
    if (data instanceof Object) {
      Object.entries(data).forEach((entry) => {
        const [k, v] = entry;
        switch (k) {
          case "lastLogin":
          case "createdAt":
          case "modifiedAt":
            if (typeof v === "string" && validDateString(v, true)) {
              this[k] = new Date(v);
            }
            break;
          case "mode":
          case "identifier":
          case "role":
          case "fullName":
            if (typeof v === "string") {
              this[k] = v;
            }
            break;
          case "active":
            if (typeof v === "boolean") {
              this[k] = v;
            }
            break;
          default:
            this[k] = v;
            break;
        }
      });
    }
  }
}

export class DisplaySettingOption {
  key = "";
  name = "";
  mode = "";
  value = "";
  show = [];
  sortIndex = 0;

  constructor(inData: any = null) {
    if (inData instanceof Object) {
      Object.entries(inData).forEach(([k, v]) => {
        if (
          typeof v === "string" &&
          ["key", "value", "name", "mode"].includes(k)
        ) {
          this[k] = v;
        } else if (v instanceof Array && k === "show") {
          this.show = v;
        }
      });
    }
  }
}

export class IDisplaySetting {
  key: string;
  type: string;
  options: DisplaySettingOption[];
  label: string;
  differ: string;
  dependency: string;
}

export class DisplaySetting implements IDisplaySetting {
  key = "";
  type = "";
  options: DisplaySettingOption[] = [];
  label = "";
  help = "";
  differ = "";
  dependency = "";
  defVal: any = null;

  constructor(inData: any = null) {
    if (inData instanceof Object) {
      Object.entries(inData).forEach(([k, v]) => {
        if (
          typeof v === "string" &&
          ["key", "label", "help", "differ", "type", "dependency"].includes(k)
        ) {
          this[k] = v;
        } else if (v instanceof Array && k === "options") {
          this.options = v.map((op) => new DisplaySettingOption(op));
        } else if (k === "defVal" && v !== null) {
          this.defVal = v;
        }
      });
      if (this.type === "rgbset") {
        this.options = this.options.map((op, opi) => {
          const modeWeight =
            op.mode === "light" ? 1000 : op.mode === "medium" ? 2000 : 3000;
          return { ...op, sortIndex: modeWeight + opi };
        });
        this.options.sort((a, b) => a.sortIndex - b.sortIndex);
      }
    }
  }

  get hasOptions() {
    return this.type !== "boolean" && this.options.length > 0;
  }

  get splitOptions() {
    return this.type === "rgbset" && this.key.includes("back");
  }

  get optionSets() {
    const modes = new Set(this.options.map((op) => op.mode));
    return [...modes].map((mk, mi) => {
      return {
        label: capitalize(mk),
        key: mk,
        itemKey: ["opt-set", mk, mi].join("-"),
        options: this.options.filter((op) => op.mode === mk),
      };
    });
  }

  get hasHelp() {
    return notEmptyString(this.help, 4);
  }

  get hasDependency() {
    return notEmptyString(this.dependency, 3);
  }

  get contrasts() {
    return notEmptyString(this.differ, 3);
  }

  get differRowKey() {
    return this.contrasts ? this.differ.split("/").shift() : "";
  }

  get differField() {
    return this.contrasts ? this.differ.split("/").pop() : "";
  }

  get showFieldSetLabel() {
    return this.type !== "boolean";
  }

  enabledOptions(rows: IDisplaySetting[] = [], values: KeyAnyValue[] = []) {
    return this.options.filter((op) => this.optionIsEnabled(op, rows, values));
  }

  matchRelRow(rows: IDisplaySetting[] = [], relKey = "") {
    const relRow = rows.find((r) => r.key === relKey);
    const hasRelRow = relRow instanceof Object;
    return { relRow, hasRelRow };
  }

  optionIsEnabled(
    op: DisplaySettingOption,
    rows: IDisplaySetting[] = [],
    values: KeyAnyValue[] = []
  ) {
    if (this.contrasts) {
      const { relRow, hasRelRow } = this.matchRelRow(rows, this.differRowKey);
      const relField = relRow instanceof Object ? this.differField : "";
      const opVal = Object.keys(op).includes(relField) ? op[relField] : ":";
      let relVal = "-";
      const valRow = values.find((v) => v.key === this.differRowKey);
      if (hasRelRow && valRow instanceof Object) {
        const relItem = relRow.options.find((op2) => op2.key == valRow.value);
        if (relItem instanceof Object) {
          relVal = relItem.mode;
        }
      }
      return opVal !== relVal;
    } else if (this.hasDependency) {
      const { relRow, hasRelRow } = this.matchRelRow(rows, this.dependency);
      const valRow = hasRelRow
        ? values.find((v) => v.key === relRow.key)
        : null;
      const relVal = valRow instanceof Object ? valRow.value : "-";
      return !hasRelRow || relVal === "-" || op.show.includes(relVal);
    } else {
      return true;
    }
  }

  isActive(
    op: DisplaySettingOption,
    setting: IDisplaySetting,
    values: KeyAnyValue[] = []
  ) {
    const valRow = values.find((v) => v.key === setting.key);
    return valRow instanceof Object && valRow.value === op.key;
  }

  matchValue(displayOptions: any) {
    const keys =
      displayOptions instanceof Object ? Object.keys(displayOptions) : [];
    return keys.includes(this.key)
      ? displayOptions[this.key]
      : this.matchDefVal();
  }

  matchDefVal(): string | boolean {
    switch (this.type) {
      case "boolean":
        return this.defVal !== null ? this.defVal : false;
      case "rgbset":
        return this.defVal !== null ? this.defVal : "#000";
      default:
        return this.defVal !== null ? this.defVal : "";
    }
  }
}

export class SiteSchema {
  labels: Map<string, string> = new Map();
  settings: Map<string, any> = new Map();
  displayOptions: Map<string, DisplaySetting[]> = new Map();

  mode = "student";
  snippets = [];
  courses = [];
  themes = [];
  programmes = [];
  schools = [];
  timedOut = false;
  project: SimpleProject = { ...defaultSimpleProject };

  constructor(inData = null) {
    if (inData instanceof Object) {
      const keys = Object.keys(inData);
      if (keys.includes("labels")) {
        this.assignLabels(inData.labels);
      }
      if (keys.includes("settings") && inData.settings instanceof Array) {
        this.assignSettings(inData.settings);
      }
      if (keys.includes("snippets") && inData.snippets instanceof Array) {
        this.assignSnippets(inData.snippets);
      }
      if (
        keys.includes("displayOptions") &&
        inData.displayOptions instanceof Object
      ) {
        this.assignDisplayOptions(inData.displayOptions);
      }
      if (keys.includes("schools") && inData.schools instanceof Array) {
        this.assignSchools(inData.schools);
      }
      if (keys.includes("programmes") && inData.programmes instanceof Array) {
        this.assignProgrammes(inData.programmes);
      }
      if (keys.includes("courses") && inData.courses instanceof Array) {
        this.assignCourses(inData.courses);
      }
      if (keys.includes("themes") && inData.themes instanceof Array) {
        this.themes = inData.themes.filter((row) => row instanceof Object);
      }
      if (keys.includes("mode") && notEmptyString(inData.mode)) {
        this.mode = inData.mode;
      }
    }
  }

  assignSettings(settings: Array<any>) {
    settings.forEach((row) => {
      if (row instanceof Object) {
        this.settings.set(row.key, row.value);
      }
    });
  }

  assignSnippets(snippets: Array<any>) {
    this.snippets = snippets.filter((sn) => {
      let valid = sn instanceof Object;
      if (valid) {
        const keys = Object.keys(sn);
        valid =
          keys.includes("key") &&
          keys.includes("value") &&
          keys.includes("format");
      }
      return valid;
    });
  }

  assignDisplayOptions(displayOptObj: any = null) {
    if (displayOptObj instanceof Object) {
      Object.entries(displayOptObj).forEach(([k, rows]) => {
        if (rows instanceof Array) {
          this.displayOptions.set(
            k,
            rows.map((r) => new DisplaySetting(r))
          );
        }
      });
    }
  }

  assignCourses(courses: Array<any>) {
    this.courses = courses.filter((sn) => sn instanceof Object);
  }

  assignSchools(schools: Array<any>) {
    this.schools = schools.filter((sn) => sn instanceof Object);
  }

  assignProgrammes(programmes: Array<any>) {
    this.programmes = programmes.filter((sn) => sn instanceof Object);
  }

  assignLabels(labels = null) {
    if (labels instanceof Object) {
      Object.entries(labels).forEach((entry) => {
        const [key, value] = entry;
        if (typeof value === "string") {
          this.labels.set(key, value);
        }
      });
    }
  }

  setting(key: string) {
    return this.settings.get(key);
  }

  label(key: string) {
    return this.labels.get(key);
  }

  get definedThemeSlugs(): string[] {
    const slugs: string[] = [];
    if (this.themes instanceof Array && this.themes.length > 0) {
      this.themes.forEach((item) => {
        slugs.push(item.slug);
        if (item.children instanceof Array && item.children.length > 0) {
          item.children.forEach((sub) => {
            slugs.push(sub.slug);
          });
        }
      });
    }
    return slugs;
  }
}
